import { StorageService } from '@/lib/StorageService';
import type { ToolImage } from '../types/LabelingTypes';
import { sortByCapturedAt } from '../dichotomous/dichotomousSearch';
import type { DichotomousState } from '../dichotomous/dichotomousSearch';

const storage = new StorageService();

// ---------- autoAdvance preference ----------

export function loadAutoAdvance(): boolean {
  return storage.getLabelingAutoAdvance();
}

export function saveAutoAdvance(value: boolean): void {
  storage.setLabelingAutoAdvance(value);
}

// ---------- dichotomous search snapshot ----------

/**
 * Restores an in-progress dichotomous search for the experiment, or `null` when nothing usable
 * is stored.
 *
 * Every index inside a `DichotomousState` (`lo`, `hi`, `answers`, `verifyQueue`, `conflict`)
 * addresses `state.keys` — the capturedAt-sorted key list captured when the search started, minus
 * the images set aside in `state.unreadable`. If
 * the experiment's image set changed since (an image imported, deleted, or moved by a parallel
 * session), those indices now point at the wrong images and a resumed search would silently
 * label the wrong things. There is no safe partial recovery, so any drift — a length change *or*
 * a single differing key — discards the whole snapshot and the search restarts.
 */
export function loadDichotomousPersisted(
  sessionId: string,
  items: ToolImage[]
): DichotomousState | null {
  const stored = storage.getLabelingDichotomousBySession(sessionId);
  if (!stored) return null;

  const state = stored.state;
  // Version 1 snapshots predate unreadable images (`keys` was the whole slice) and are dropped.
  if (!state || state.version !== 2) return null;
  if (!Array.isArray(state.keys) || !Array.isArray(state.unreadable)) return null;

  // `keys` and `unreadable` partition the slice: the images still in play must be the current
  // list minus the unreadable ones, in the same order, and no unreadable key may have vanished.
  const unreadable = new Set(state.unreadable);
  const currentKeys = sortByCapturedAt(items).map((item) => item.key);
  if (state.keys.length + unreadable.size !== currentKeys.length) return null;
  const inPlay = currentKeys.filter((key) => !unreadable.has(key));
  if (inPlay.length !== state.keys.length) return null;
  if (state.keys.some((key, index) => key !== inPlay[index])) return null;

  return state;
}

export function saveDichotomousPersisted(sessionId: string, state: DichotomousState): void {
  storage.setLabelingDichotomousBySession(sessionId, { state });
}

export function clearDichotomousPersisted(sessionId: string): void {
  storage.removeLabelingDichotomousBySession(sessionId);
}
